import { Dog } from '@/types/dog';
import {
  MatingProposalResponse,
  MaleDogProposal,
  GeminiMatingResponse,
  MatingProposalOptions,
} from '@/types/ai';
import { AIAnalysisHistoryInput, AIAnalysisSuggestion } from '@/types/aiManagement';
import { promptBuilder } from './promptBuilder';
import { getGeminiModel, type GenerativeModel } from './geminiClient';
import { GEMINI_CONFIG, AI_MATING_CONFIG, DISCLAIMER, AI_ANALYSIS_DAILY_LIMIT } from './config';
import { inbreedingCalculator, getRiskLevel } from '@/services/breeding/inbreedingCalculator';
import { pedigreeService } from '@/services/firebase/pedigreeService';
import { dogService } from '@/services/firebase/dogService';
import { birthRecordService } from '@/services/firebase/birthRecordService';
import { matingRecordService } from '@/services/firebase/matingRecordService';
import { aiAnalysisHistoryService } from '@/services/firebase/aiAnalysisHistoryService';

/**
 * AI交配提案サービス
 * Gemini APIを使用してメス犬に最適なオス犬を提案する
 */
export class AIMatingService {
  private model: GenerativeModel | null = null;

  /**
   * Gemini API（Firebase AI Logic経由）を初期化
   */
  private initialize(): void {
    if (this.model) {
      return;
    }

    this.model = getGeminiModel({
      model: GEMINI_CONFIG.model,
      maxOutputTokens: GEMINI_CONFIG.maxOutputTokens,
      temperature: GEMINI_CONFIG.temperature,
    });
  }

  /**
   * 交配提案を取得
   * @param femaleId 対象メス犬のID
   * @param userId ユーザーID
   * @param options オプション
   * @returns 交配提案レスポンス
   */
  async getProposals(
    femaleId: string,
    userId: string,
    options: MatingProposalOptions = {}
  ): Promise<MatingProposalResponse> {
    const startTime = Date.now();
    let femaleName = '';
    let candidates: Dog[] = [];
    let rawResponse = '';

    try {
      // 日次上限チェック
      const todayCount = await aiAnalysisHistoryService.getTodayCount(userId);
      if (todayCount >= AI_ANALYSIS_DAILY_LIMIT) {
        throw new Error(
          `本日のAI分析上限（${AI_ANALYSIS_DAILY_LIMIT}回）に達しました。明日以降にお試しください。`
        );
      }

      // APIを初期化
      this.initialize();

      // オプションをデフォルト値とマージ
      const mergedOptions = {
        maxCandidates: options.maxCandidates ?? AI_MATING_CONFIG.maxCandidates,
        avoidInbreeding:
          options.avoidInbreeding ?? AI_MATING_CONFIG.avoidInbreeding,
        minimumGenerations:
          options.minimumGenerations ?? AI_MATING_CONFIG.minimumGenerations,
      };

      // メス犬の情報を取得
      const female = await dogService.getDog(femaleId, userId);
      if (!female) {
        const errorResponse = this.createErrorResponse(femaleId, '', '対象のメス犬が見つかりません');
        await this.saveHistoryOnError(userId, femaleId, '', [], '対象のメス犬が見つかりません', startTime);
        return errorResponse;
      }

      femaleName = female.name;

      if (female.gender !== 'female') {
        const errorResponse = this.createErrorResponse(
          femaleId,
          female.name,
          '対象の犬はメスではありません'
        );
        await this.saveHistoryOnError(userId, femaleId, femaleName, [], '対象の犬はメスではありません', startTime);
        return errorResponse;
      }

      // 候補オス犬を取得
      const allMales = await dogService.getDogs(userId, { gender: 'male' });
      const activeMales = allMales.filter((dog) => dog.status === 'active');

      if (activeMales.length === 0) {
        const errorResponse = this.createErrorResponse(
          femaleId,
          female.name,
          '候補となるオス犬がいません'
        );
        await this.saveHistoryOnError(userId, femaleId, femaleName, [], '候補となるオス犬がいません', startTime);
        return errorResponse;
      }

      // 候補を絞り込み（最大数まで）
      candidates = activeMales.slice(0, mergedOptions.maxCandidates);

      // 出産記録と交配記録を取得
      const [birthRecords, matingRecords] = await Promise.all([
        birthRecordService.getBirthRecords(userId),
        matingRecordService.getMatingRecords(userId),
      ]);

      // 祖先マップを取得（メス犬と全候補オス犬）
      const allDogIds = [femaleId, ...candidates.map((c) => c.id)];
      const ancestorMap = await pedigreeService.getAncestorMapForMultipleDogs(
        allDogIds,
        userId,
        AI_MATING_CONFIG.ancestorGenerations
      );

      // 各候補オス犬との近親係数を計算
      const inbreedingCoefficients = new Map<string, number>();
      for (const male of candidates) {
        const coefficient = inbreedingCalculator.calculateCoefficient(
          male.id,
          femaleId,
          ancestorMap
        );
        inbreedingCoefficients.set(male.id, coefficient);
      }

      // プロンプトを生成
      const prompt = promptBuilder.buildMatingProposalPrompt(
        female,
        candidates,
        birthRecords,
        matingRecords,
        inbreedingCoefficients
      );

      // Gemini APIを呼び出し
      rawResponse = await this.callGeminiAPI(prompt);

      // レスポンスをパース
      const proposals = this.parseGeminiResponse(
        rawResponse,
        candidates,
        inbreedingCoefficients
      );

      // 履歴を保存（成功）
      await this.saveHistoryOnSuccess(
        userId,
        femaleId,
        femaleName,
        candidates,
        proposals,
        rawResponse,
        startTime
      );

      return {
        success: true,
        femaleId,
        femaleName: female.name,
        proposals,
        analyzedAt: new Date().toISOString(),
        disclaimer: DISCLAIMER,
      };
    } catch (error) {
      console.error('AI交配提案エラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'AI分析中にエラーが発生しました';

      // 履歴を保存（エラー）
      await this.saveHistoryOnError(
        userId,
        femaleId,
        femaleName,
        candidates,
        errorMessage,
        startTime,
        rawResponse
      );

      return this.createErrorResponse(
        femaleId,
        femaleName,
        errorMessage
      );
    }
  }

  /**
   * 成功時に履歴を保存
   */
  private async saveHistoryOnSuccess(
    userId: string,
    femaleId: string,
    femaleName: string,
    candidates: Dog[],
    proposals: MaleDogProposal[],
    rawResponse: string,
    startTime: number
  ): Promise<void> {
    try {
      const suggestions: AIAnalysisSuggestion[] = proposals.map((p) => ({
        maleId: p.maleId,
        maleName: p.maleName,
        score: p.overallScore,
        reasons: p.reasons,
        warnings: p.warnings,
      }));

      const historyData: AIAnalysisHistoryInput = {
        userId,
        promptId: 'default', // TODO: プロンプト選択機能実装後に変更
        promptVersion: 1,
        input: {
          femaleId,
          femaleName,
          candidateIds: candidates.map((c) => c.id),
          candidateNames: candidates.map((c) => c.name),
        },
        output: {
          suggestions,
          rawResponse,
        },
        executionTime: Date.now() - startTime,
        status: 'success',
      };

      await aiAnalysisHistoryService.saveHistory(historyData);
    } catch (historyError) {
      // 履歴保存の失敗はメイン処理に影響させない
      console.error('分析履歴の保存に失敗:', historyError);
    }
  }

  /**
   * エラー時に履歴を保存
   */
  private async saveHistoryOnError(
    userId: string,
    femaleId: string,
    femaleName: string,
    candidates: Dog[],
    errorMessage: string,
    startTime: number,
    rawResponse?: string
  ): Promise<void> {
    try {
      const historyData: AIAnalysisHistoryInput = {
        userId,
        promptId: 'default',
        promptVersion: 1,
        input: {
          femaleId,
          femaleName,
          candidateIds: candidates.map((c) => c.id),
          candidateNames: candidates.map((c) => c.name),
        },
        output: {
          suggestions: [],
          rawResponse,
        },
        executionTime: Date.now() - startTime,
        status: 'error',
        errorMessage,
      };

      await aiAnalysisHistoryService.saveHistory(historyData);
    } catch (historyError) {
      // 履歴保存の失敗はメイン処理に影響させない
      console.error('分析履歴の保存に失敗:', historyError);
    }
  }

  /**
   * Gemini APIを呼び出し
   */
  private async callGeminiAPI(prompt: string): Promise<string> {
    if (!this.model) {
      throw new Error('Gemini APIが初期化されていません');
    }

    const result = await this.model.generateContent(prompt);
    const response = result.response;
    const text = response.text();

    return text;
  }

  /**
   * Geminiのレスポンスをパース
   */
  private parseGeminiResponse(
    responseText: string,
    candidates: Dog[],
    inbreedingCoefficients: Map<string, number>
  ): MaleDogProposal[] {
    try {
      // JSONブロックを抽出
      const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
      const jsonStr = jsonMatch ? jsonMatch[1] : responseText;

      // JSONをパース
      const parsed: GeminiMatingResponse = JSON.parse(jsonStr.trim());

      if (!parsed.suggestions || !Array.isArray(parsed.suggestions)) {
        throw new Error('Invalid response format: suggestions array not found');
      }

      // 候補犬のマップを作成
      const candidateMap = new Map(candidates.map((c) => [c.id, c]));

      // 提案を変換
      const proposals: MaleDogProposal[] = parsed.suggestions
        .filter((s) => candidateMap.has(s.maleId))
        .map((suggestion) => {
          const male = candidateMap.get(suggestion.maleId)!;
          const inbreedingCoef =
            inbreedingCoefficients.get(suggestion.maleId) || 0;

          return {
            maleId: suggestion.maleId,
            maleName: male.name,
            malePhotoUrl: male.mainPhotoUrl,
            overallScore: Math.min(100, Math.max(0, suggestion.score)),
            scoreBreakdown: {
              geneticDiversity: Math.min(
                100,
                Math.max(0, suggestion.scoreBreakdown?.geneticDiversity || 50)
              ),
              breedingHistory: Math.min(
                100,
                Math.max(0, suggestion.scoreBreakdown?.breedingHistory || 50)
              ),
              traitMatch: Math.min(
                100,
                Math.max(0, suggestion.scoreBreakdown?.traitMatch || 50)
              ),
            },
            inbreedingCoefficient: inbreedingCoef,
            inbreedingRisk: getRiskLevel(inbreedingCoef),
            reasons: suggestion.reasons || [],
            warnings: suggestion.warnings || [],
            expectedTraits: suggestion.expectedTraits || '',
          };
        });

      // スコア順にソート
      proposals.sort((a, b) => b.overallScore - a.overallScore);

      return proposals;
    } catch (error) {
      console.error('Geminiレスポンスのパースエラー:', error);
      console.error('Response text:', responseText);

      // パースに失敗した場合は、基本的なスコアリングでフォールバック
      return this.createFallbackProposals(candidates, inbreedingCoefficients);
    }
  }

  /**
   * フォールバック用の基本的な提案を作成
   */
  private createFallbackProposals(
    candidates: Dog[],
    inbreedingCoefficients: Map<string, number>
  ): MaleDogProposal[] {
    return candidates.map((male) => {
      const inbreedingCoef = inbreedingCoefficients.get(male.id) || 0;
      const inbreedingRisk = getRiskLevel(inbreedingCoef);

      // 近親係数に基づいてスコアを計算
      let geneticDiversityScore = 100;
      if (inbreedingRisk === 'caution') geneticDiversityScore = 70;
      if (inbreedingRisk === 'warning') geneticDiversityScore = 40;
      if (inbreedingRisk === 'danger') geneticDiversityScore = 10;

      const overallScore = Math.round(
        geneticDiversityScore * 0.4 + 50 * 0.3 + 50 * 0.3
      );

      return {
        maleId: male.id,
        maleName: male.name,
        malePhotoUrl: male.mainPhotoUrl,
        overallScore,
        scoreBreakdown: {
          geneticDiversity: geneticDiversityScore,
          breedingHistory: 50,
          traitMatch: 50,
        },
        inbreedingCoefficient: inbreedingCoef,
        inbreedingRisk,
        reasons: ['AI分析に失敗したため、近親係数に基づく基本スコアを表示しています'],
        warnings:
          inbreedingRisk !== 'safe'
            ? [`近親係数が${(inbreedingCoef * 100).toFixed(1)}%です`]
            : [],
        expectedTraits: 'データ不足のため予測できません',
      };
    }).sort((a, b) => b.overallScore - a.overallScore);
  }

  /**
   * エラーレスポンスを作成
   */
  private createErrorResponse(
    femaleId: string,
    femaleName: string,
    errorMessage: string
  ): MatingProposalResponse {
    return {
      success: false,
      femaleId,
      femaleName,
      proposals: [],
      analyzedAt: new Date().toISOString(),
      disclaimer: DISCLAIMER,
      error: errorMessage,
    };
  }
}

// シングルトンインスタンスをエクスポート
export const aiMatingService = new AIMatingService();
